import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { animated } from 'react-spring'
import { BlockItemShape, LabelLocationShape, LabelSizeShape } from '../propTypes'
import { getClassName, getFormatString, getStyleByDepth } from '../utils'
import { useVisualBlocksContext } from '../VisualBlocksContext'
import BlockItemHoverDetails from './BlockItemHoverDetails'
import BlockItemLabel from './BlockItemLabel'
import BlockItemSubBlocks from './BlockItemSubBlocks'
import { BlockItemSummaryTooltip } from './BlockItemSummaryTooltip'

export default function BlockItem ({
  depth = 0,
  label,
  value,
  color,
  textColor,
  labelLocation,
  details = [],
  fixedWidth,
  allocation,
  labelSize = 'md'
}) {
  const [isHovered, setIsHovered] = useState(false)
  const [subItemHovered, setSubItemHovered] = useState(null)
  const { formats, classNames, styles, maxBlockDepth } =
    useVisualBlocksContext()

  const formatString = getFormatString(formats, depth)
  const className = getClassName(classNames, depth)
  const blockStyle = useMemo(
    () => getStyleByDepth(styles, 'blocks', depth),
    [styles, depth]
  )
  const rowStyle = useMemo(
    () => getStyleByDepth(styles, 'rows', depth),
    [styles, depth]
  )

  return (
    <div
      className='flex gap-2 relative'
      style={{
        minHeight: '110px',
        ...rowStyle,
        flex: `${allocation ? Math.max(allocation, 0.1) : 1} 0`
      }}
    >
      <animated.div
        className={clsx(
          'radius-lg bg-neutral-400 flex items-center justify-center',
          className
        )}
        style={{
          background: color,
          overflow: 'hidden',
          flex: fixedWidth ? `0 0 ${fixedWidth}` : 1,
          ...blockStyle
        }}
        onMouseOver={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {labelLocation === 'inside' && (
          <div className='flex items-center justify-start w-full h-full px-4'>
            <BlockItemLabel
              labelSize={labelSize}
              label={label}
              value={value}
              formatString={formatString}
              backgroundColor={color}
              textColor={textColor}
              location={labelLocation}
            />
          </div>
        )}

        {!!details?.length && maxBlockDepth <= depth && (
          <BlockItemHoverDetails
            items={details}
            depth={depth}
            visible={isHovered}
            onHoverChange={setSubItemHovered}
            color={color}
          />
        )}

        {!!details?.length && depth > 0 && depth <= maxBlockDepth && (
          <BlockItemSummaryTooltip
            label={label}
            value={value}
            color={color}
            details={details}
            depth={depth}
            itemHovered={subItemHovered}
            visible={isHovered}
          />
        )}
      </animated.div>

      {!!details?.length && maxBlockDepth > depth && (
        <BlockItemSubBlocks depth={depth + 1} items={details} />
      )}

      {labelLocation === 'outside' && (
        <div className='flex items-center pr-2'>
          <BlockItemLabel
            labelSize={labelSize}
            label={label}
            value={value}
            formatString={formatString}
          />
        </div>
      )}
    </div>
  )
}

BlockItem.propTypes = {
  depth: PropTypes.number,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  labelLocation: LabelLocationShape.isRequired,
  details: PropTypes.arrayOf(BlockItemShape),
  fixedWidth: PropTypes.string,
  allocation: PropTypes.string,
  labelSize: LabelSizeShape
}
